/**
 * Cash boxes and banks with what is in each, how it got there, and the
 * transfers between them. The banks the supplier payment form uses are the
 * same rows, so their supplier payments come off the balance here.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import RowActions from "components/RowActions";

import { BoxDialog, TransferDialog } from "./BoxDialogs";
import StatementDialog from "./StatementDialog";
import { api, problemOf, money } from "./api";

const cell = { fontSize: 14, whiteSpace: "nowrap" };

function BoxesTab({ boxes, onChanged }) {
  // {box} to edit, {} for new, null when closed
  const [editing, setEditing] = useState(null);
  const [moving, setMoving] = useState(false);
  const [statement, setStatement] = useState(null);
  const [transfers, setTransfers] = useState([]);

  const loadTransfers = () =>
    api
      .get("/transfers")
      .then((rows) => setTransfers(Array.isArray(rows) ? rows : []))
      .catch(() => setTransfers([]));

  useEffect(() => {
    loadTransfers();
  }, [boxes]);

  const remove = async (b) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${b.name}?`)) return;
    try {
      await api.del(`/boxes/${b.id}`);
      onChanged();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(problemOf(e, "Could not delete"));
    }
  };

  const removeTransfer = async (t) => {
    const what = `${money(t.amount)} ${t.from_currency} from ${t.from_name} to ${t.to_name}`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the transfer of ${what}?`)) return;
    try {
      await api.del(`/transfers/${t.id}`);
      onChanged();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(problemOf(e, "Could not delete"));
    }
  };

  const signed = (v, tone) => (
    <SuiTypography
      component="span"
      sx={({ palette }) => ({
        fontSize: 14,
        color: Number(v) ? palette[tone].main : "inherit",
        opacity: Number(v) ? 1 : 0.4,
      })}
    >
      {money(v)}
    </SuiTypography>
  );

  return (
    <>
      <SuiBox display="flex" alignItems="center" flexWrap="wrap" gap={1.5} px={3} py={2}>
        <SuiTypography variant="h6">Boxes &amp; Banks · الصناديق والمصارف</SuiTypography>
        <SuiBox flex={1} />
        <Button variant="outlined" color="primary" onClick={() => setMoving(true)}>
          Transfer
        </Button>
        <Button variant="contained" color="primary" onClick={() => setEditing({})}>
          Add Box / Bank
        </Button>
      </SuiBox>

      <SuiBox sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 1150 }}>
          <TableHead>
            <TableRow>
              <TableCell align="center">Name</TableCell>
              <TableCell align="center">Kind</TableCell>
              <TableCell align="center">Opening</TableCell>
              <TableCell align="center">Receipts</TableCell>
              <TableCell align="center">Payments</TableCell>
              <TableCell align="center">Supplier payments</TableCell>
              <TableCell align="center">Transfers in / out</TableCell>
              <TableCell align="center">Balance</TableCell>
              <TableCell align="center">Operations</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {boxes.map((b) => (
              <TableRow key={b.id} sx={{ opacity: b.is_active ? 1 : 0.55 }}>
                <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                  {b.name}
                  {!b.is_active && (
                    <Chip size="small" label="inactive" sx={{ ml: 1, fontSize: 11 }} />
                  )}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  {b.kind === "cash" ? "Cash box" : "Bank"}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  {money(b.opening_balance)}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  {signed(b.receipts, "success")}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  {signed(b.payments, "error")}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  {signed(b.supplier_paid, "error")}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  {signed(b.transfers_in, "success")}
                  {" / "}
                  {signed(b.transfers_out, "error")}
                </TableCell>
                <TableCell
                  align="center"
                  sx={({ palette }) => ({
                    ...cell,
                    fontWeight: 800,
                    fontSize: 15,
                    color: b.balance < 0 ? palette.error.main : palette.dark.main,
                  })}
                >
                  {`${money(b.balance)} ${b.currency}`}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  <RowActions
                    actions={[
                      {
                        icon: "receipt_long",
                        label: "Statement",
                        tone: "info",
                        onClick: () => setStatement(b),
                      },
                      { icon: "edit", label: "Edit", onClick: () => setEditing({ box: b }) },
                      { icon: "delete", label: "Delete", tone: "danger", onClick: () => remove(b) },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
            {boxes.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <SuiTypography variant="button" color="text">
                    No boxes yet
                  </SuiTypography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </SuiBox>

      <SuiBox px={3} pt={4} pb={1}>
        <SuiTypography variant="h6">Transfers · التحويلات</SuiTypography>
      </SuiBox>
      <SuiBox sx={{ overflowX: "auto" }} pb={2}>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell align="center">No.</TableCell>
              <TableCell align="center">Date</TableCell>
              <TableCell align="center">From</TableCell>
              <TableCell align="center">To</TableCell>
              <TableCell align="center">Sent</TableCell>
              <TableCell align="center">Received</TableCell>
              <TableCell align="center">Reference / Notes</TableCell>
              <TableCell align="center">Operations</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transfers.map((t) => (
              <TableRow key={t.id}>
                <TableCell
                  align="center"
                  sx={{ ...cell, fontWeight: 700 }}
                >{`TR-${t.id}`}</TableCell>
                <TableCell align="center" sx={cell}>
                  {t.date}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  {t.from_name}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  {t.to_name}
                </TableCell>
                <TableCell align="center" sx={cell}>{`${money(t.amount)} ${
                  t.from_currency
                }`}</TableCell>
                <TableCell align="center" sx={cell}>{`${money(t.to_amount)} ${
                  t.to_currency
                }`}</TableCell>
                <TableCell align="center" sx={{ fontSize: 13 }}>
                  {[t.reference, t.notes].filter(Boolean).join(" · ") || "-"}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  <RowActions
                    actions={[
                      {
                        icon: "delete",
                        label: "Delete",
                        tone: "danger",
                        onClick: () => removeTransfer(t),
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
            {transfers.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <SuiTypography variant="button" color="text">
                    No transfers yet
                  </SuiTypography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </SuiBox>

      <BoxDialog
        open={Boolean(editing)}
        box={editing?.box || null}
        onClose={() => setEditing(null)}
        onSaved={onChanged}
      />
      <TransferDialog
        open={moving}
        boxes={boxes}
        onClose={() => setMoving(false)}
        onSaved={onChanged}
      />
      <StatementDialog box={statement} onClose={() => setStatement(null)} />
    </>
  );
}

BoxesTab.propTypes = {
  boxes: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChanged: PropTypes.func.isRequired,
};

export default BoxesTab;
